/**
 * Almost the same as a normal product-size-select but resets firmness when a
 * size is selected.
 */
class MattressSizeSelect extends ProductSizeSelect {
  constructor() {
    super();
  }

  /**
   * Selecting a size button means choosing a new size but also resetting any
   * addons and firmnesses because they are both specific to sizes.
   * This is almost the same as the normal product-size-select but resets firmness
   *
   * @param button The size button that was selected
   */
  select(button) {
    const index = Array.from(this.sizeBtns).indexOf(button);
    const newPosition = index * -100;
    this.menu.style.top = newPosition + "%";

    // Set new selected button
    this.sizeBtns.forEach((b) =>
      b.classList.remove("MattressSizeSelect__SizeBtn--selected")
    );
    button.classList.add("MattressSizeSelect__SizeBtn--selected");

    // Clear any addons (since a new size was selected)
    window.NolahPDPState.clearAddons();

    // Update state and selector
    this.selector.innerHTML = button.innerHTML;

    const newSize = button.dataset.value;
    const optionName = button.dataset.option;
    window.NolahPDPState.setSizeAndResetFirmness(newSize,optionName);

    this.closeMenu();
  }

  /**
   * When syncing after option changes, we need to also double check and update
   * the price labels in the size select if the product itself has changed to
   * the hybrid version (applicable to mattresses with hybrid foam upsells).
   */
  syncToCurrentVariant(e) {
    super.syncToCurrentVariant();

    // If the product JSON changed, we will want to update the price labels
    // in the size select.
    if (e && e.detail && e.detail.productJSONChanged) {
      this.syncPriceLabelsToState();
    }
  }

  /**
   * Sync the price labels of all the size buttons to the current selected product.
   * This is only for when the hybrid foam upsell is selected, which means an
   * entirely new product was chosen to be added to cart.
   */
  syncPriceLabelsToState() {
    const productJSON = window.NolahPDPState.productJSON;
    const sizeKey = window.NolahPDPState.getOptionKey("Size");

    // Go through each size button and update its price labels
    this.sizeBtns.forEach((btn) => {
      const size = btn.dataset.value;
      const foundVar = productJSON.variants.find((v) => v[sizeKey] === size);
      const priceTag = btn.querySelector(".MattressSizeSelect__PriceTag");
      let lowerPrice, higherPrice;

      // Be wary of multiplying the Twin XL price by two for Split King
      if (!foundVar && size === "Split King") {
        const twinXLVar = productJSON.variants.find(
          (v) => v[sizeKey] === "Twin XL"
        );
        lowerPrice = window.NUtils.formatMoney(
          window.NUtils.getVariantPrice(twinXLVar, false) * 2
        );
        higherPrice = window.NUtils.formatMoney(
          window.NUtils.getVariantStrikePrice(twinXLVar, false) * 2
        );
      } else {
        lowerPrice = window.NUtils.getVariantPrice(foundVar, true);
        higherPrice = window.NUtils.getVariantStrikePrice(foundVar, true);
      }

      priceTag.innerHTML = `<span class="StrikeLabel">${higherPrice}</span><b>${lowerPrice}</b>`;
    });

    // Update the selector's price label
    const currentPriceTag = this.selector.querySelector(
      ".MattressSizeSelect__PriceTag"
    );
    const currentVar = window.NolahPDPState.currentVariant;
    const lowerPrice = window.NUtils.formatMoney(
      window.NolahPDPState.getPrice(currentVar)
    );
    const higherPrice = window.NUtils.formatMoney(
      window.NolahPDPState.getComparePrice(currentVar)
    );
    currentPriceTag.innerHTML = `<span class="StrikeLabel">${higherPrice}</span><b>${lowerPrice}</b>`;
  }
}
customElements.define("mattress-size-select", MattressSizeSelect);

/**
 * Very similar to MattressOptionBlock but is used to indicate free gifts and stuff
 * that automatically come with the mattress.
 */
class MattressComesWithBlock extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
    }
  }

  cacheElements() {
    this.freeGiftsJSON = JSON.parse(
      this.querySelector("[data-pdp-free-gifts-json]").innerHTML
    );
    this.selectPopupTriggers = this.querySelectorAll(
      "[data-open-select-gift-popup]"
    );
    this.selectBtn = this.querySelector(".MattressComesWith__SelectBtn");
    this.selectPopupContents = this.querySelector(
      "[data-free-gift-popup-selector-html]"
    );
    this.onOptionChanged = this.onOptionChanged.bind(this);
  }

  attachListeners() {
    document.addEventListener("state:option-changed", this.onOptionChanged);

    this.selectPopupTriggers.forEach((trigger) =>
      trigger.addEventListener("click", (e) => {
        // Reset add btn visually
        this.selectBtn.classList.remove("BlueActionBtn--added");

        const htmlContents = this.selectPopupContents.innerHTML;
        window.GlobalNPopup.openWithContents(
          "select-free-gift-popup",
          htmlContents,
          {
            afterOpen: this.attachGiftPopupListeners.bind(this),
          }
        );
      })
    );
  }

  detachListeners() {
    document.removeEventListener(
      "state:option-changed",
      this.updateStrikePrice
    );

    // TODO Remove popup triggers as well
  }

  onOptionChanged() {
    this.updateStrikePrice();

    // Reset popup trigger
    // this.selectBtn.classList.remove('BlueActionBtn--added');
  }

  /**
   * Adds click listeners to the "add to order" buttons in the gift popup.
   * Used in the afterOpen hook of the GlobalNPopup open function.
   */
  attachGiftPopupListeners(globalPopup) {
    const addBtns = globalPopup.popup.querySelectorAll(
      ".MatGiftPopup__ItemAddBtn"
    );
    addBtns.forEach((btn) =>
      btn.addEventListener("click", () => {
        // Find the full product object matching the clicked gift
        const productID = parseInt(btn.dataset.productId);
        const productObj = this.freeGiftsJSON.find((p) => p.id === productID);
        if (!productObj) {
          throw "Free gift JSON improperly set up: could not match product ID.";
          window.alert(
            "Sorry, you may be experiencing a technical issue with our free gift popup. Please contact us for help."
          );
        }

        // See if the button is specifically choosing a variant too (optional).
        let variantID = null;
        if (btn.dataset.variantId) {
          variantID = parseInt(btn.dataset.variantId);
        }

        this.selectGift(productObj, variantID);

        globalPopup.close();
      })
    );
  }

  /**
   * Update the DOM with the new product and mark it to be added in NolahPDPState.
   */
  selectGift(product, variantID) {
    window.NolahPDPState.setFreeGift(product, variantID);

    const heading = this.querySelector(".MattressOptionBlock__SummaryHeading");
    const imageEl = this.querySelector(".MattressOptionBlock__Image img");

    // Weighted blankets offer a choice so we put what was selected in the title.
    if (product.id === window.weightedBlanketProdID && variantID) {
      const variantObj = product.variants.find((v) => v.id === variantID);
      if (variantObj) {
        heading.innerHTML = `Weighted Blanket: ${variantObj.title}`;
      }
    } else {
      heading.innerHTML = product.heading;
    }

    imageEl.src = product.thumb;
    this.selectBtn.classList.add("BlueActionBtn--added");

    this.updateStrikePrice();
  }

  updateStrikePrice() {
    const freeGiftValue = window.NolahPDPState.getMatchingGiftValue();
    const strikePrice = this.querySelector(
      ".MattressOptionBlock__FreePriceTag .StrikeLabel"
    );
    strikePrice.innerHTML = NUtils.formatMoney(freeGiftValue);
  }
}
customElements.define("mattress-comes-with-block", MattressComesWithBlock);

class MattressOptionBlock extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.syncToCurrentVariantSize();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.update = this.update.bind(this);
    this.atcBtnClickHandler = this.atcBtnClickHandler.bind(this);
    this.syncToCurrentVariantSize = this.syncToCurrentVariantSize.bind(this);

    // On mobile, upsells are added immediately
    this.mql = window.matchMedia("(max-width: 768px)");
    this.isShowingMobileView = this.mql.matches;

    this.addBtn = this.querySelector(".MattressOptionBlock__AddBtn");
    this.salesFlag = this.querySelector(".MattressOptionBlock__FloatingFlag");
    this.priceLabel = this.querySelector(".MattressOptionBlock__Price");
    this.comparePriceLabel = this.querySelector(
      ".MattressOptionBlock__PriceTag .StrikeLabel"
    );
    this.popupTriggers = this.querySelectorAll("[data-open-info-popup]");
    this.popupContents = this.querySelector("[data-info-popup-contents]");
    this.upsellInfoJSON = JSON.parse(
      this.querySelector("[data-pdp-upsell-info-json]").innerHTML
    );
    this.productID = parseInt(this.dataset.productId);
    this.isAdjWallGlide = this.productID === window.adjBaseWWallGlideProdID;
  }

  update(e) {
    this.isShowingMobileView = e.matches;
  }

  /**
   * If applicable, find the addon's variant that matches the size of the current
   * variant and update all important labels within this option block.
   */
  syncToCurrentVariantSize() {
    const matchingVariant = this.getMatchingVariant();

    // If options changed, uncheck selected buttons and styles
    this.removeSelectedStyles();

    // Reset the add btn (possibly disabled if in mobile and added)
    this.addBtn.disabled = false;
    delete this.addBtn.dataset.addedId;

    // First gray out if there are no variants that match the size
    if (!matchingVariant) {
      this.setSoldOutAppearance(true);
      return;
    } else {
      this.setSoldOutAppearance(false);
    }

    // Update prices in the block
    const higherPrice = Math.max(
      matchingVariant.price,
      matchingVariant.compare_at_price
    );
    const lowerPrice = Math.min(
      matchingVariant.price,
      matchingVariant.compare_at_price
    );
    // this.priceLabel.innerHTML = NUtils.formatMoney(lowerPrice * (1 - parseFloat( {{ settings.price_multiplier }} ) / 100));
    // this.comparePriceLabel.innerHTML = NUtils.formatMoney(lowerPrice);
    // console.log(NUtils.formatMoney(lowerPrice * (1 - parseFloat( {{ settings.price_multiplier }} ) / 100)));
    this.priceLabel.innerHTML = NUtils.formatMoney(lowerPrice);
    this.comparePriceLabel.innerHTML = NUtils.formatMoney(higherPrice);

    // Update sales flag
    const salesAmount = higherPrice - lowerPrice;
    if (salesAmount > 0) {
      this.salesFlag.innerHTML = `Save ${NUtils.formatMoney(
        higherPrice - lowerPrice
      )}`;
      this.salesFlag.style.opacity = "1";
    } else {
      this.salesFlag.style.opacity = "0";
    }
  }

  setSoldOutAppearance(isSoldOut) {
    if (isSoldOut) {
      this.classList.add("MattressOptionBlock--soldOut");
      this.salesFlag.style.opacity = "0";
    } else {
      this.classList.remove("MattressOptionBlock--soldOut");
      this.salesFlag.style.opacity = "1";
    }
  }

  getMatchingVariant() {
    const currentSize = window.NolahPDPState.getOptionByName("Size");
    let matchingVariant = this.upsellInfoJSON.variants.find((v) => {
      return v.options.includes(currentSize);
    });

    // Allow 'King' to match 'Split King' and 'Cal King' to match 'Split Cal King'
    // if its an adj base w wall glide.
    if (!matchingVariant && this.isAdjWallGlide) {
      if (currentSize === "King" || currentSize === "Cal King") {
        const sizeToFind = "Split " + currentSize;
        matchingVariant = this.upsellInfoJSON.variants.find((v) => {
          return v.options.includes(sizeToFind);
        });
      }
    }

    return matchingVariant;
  }

  attachListeners() {
    this.mql.addListener(this.update);
    document.addEventListener(
      "state:option-changed",
      this.syncToCurrentVariantSize
    );

    if (this.addBtn) {
      this.addBtn.addEventListener("click", this.atcBtnClickHandler);
    }

    this.popupTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        const htmlContents = this.popupContents.innerHTML;
        window.GlobalNPopup.loadWithContents(
          "product-card-addon",
          htmlContents
        );

        // Update savings flag first
        const popup = GlobalNPopup.popup;
        const savingsFlag = popup.querySelector(
          ".AddonInfoContents__SavingsFlag"
        );
        const savingsAmt = this.getSavingsAmt();
        savingsFlag.innerHTML = `SAVE ${NUtils.formatMoney(savingsAmt)}`;

        window.GlobalNPopup.open();
      });
    });
  }

  detachListeners() {
    this.mql.removeListener(this.update);
    document.removeEventListener(
      "state:option-changed",
      this.syncToCurrentVariantSize
    );
  }

  atcBtnClickHandler(e) {
    const variant = this.getMatchingVariant();
    this.toggleSelectedStyles();

    // On mobile, add to cart immediately
    if (this.isShowingMobileView) {
      this.handleAddBtnToggleForMobile(variant);
    } else {
      // On desktop, just toggle upsell in global state object.
      window.NolahPDPState.toggleAddon(variant);
    }
  }

  /**
   * On mobile, upsells are added immediately and removed from cart when clicked
   * again, as opposed to desktop, where they are marked in NolahPDPState
   * to be added with the main atc button.
   */
  async handleAddBtnToggleForMobile(variant) {
    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    this.addBtn.disabled = true;

    // If this button was already used to add an upsell, remove the upsell now.
    const addedVariantID = this.addBtn.dataset.addedId;
    if (addedVariantID) {
      // Remove the upsell.
      await window.NolahCartManager.removeUpsell(addedVariantID);

      // Refresh without opening the sidecart
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      // Clear the addedId dataset so we know it was removed
      delete this.addBtn.dataset.addedId;
    } else {
      // Otherwise, add the upsell
      await window.NolahCartManager.addItems([
        {
          id: variant.id,
          quantity: 1,
        },
      ]);

      // Store the added upsell in the add button's dataset
      this.addBtn.dataset.addedId = variant.id;
    }

    document.dispatchEvent(new CustomEvent("theme:loading:end"));
    this.addBtn.disabled = false;
  }

  getSavingsAmt() {
    const variant = this.getMatchingVariant();
    const higherPrice = Math.max(variant.price, variant.compare_at_price);
    const lowerPrice = Math.min(variant.price, variant.compare_at_price);

    return higherPrice - lowerPrice;
  }

  /**
   * Adds necessary css classes to visually mark it as selected
   */
  toggleSelectedStyles() {
    this.addBtn.classList.toggle("BlueActionBtn--added");
    this.classList.toggle("MattressOptionBlock--added");
  }

  /**
   * Adds necessary css classes to visually mark it as selected
   */
  removeSelectedStyles() {
    this.addBtn.classList.remove("BlueActionBtn--added");
    this.classList.remove("MattressOptionBlock--added");
  }
}
customElements.define("mattress-option-block", MattressOptionBlock);

class AlsoIncludesList extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      this.showViewBasedOnWidth();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;

      this.detachListeners();
    }
  }

  cacheElements() {
    this.updateOptOutAdded = this.updateOptOutAdded.bind(this);
    this.popupListeners = [];

    // Media query list listener added when connected to DOM
    this.mql = window.matchMedia("(max-width: 768px)");
    this.mqlListener = this.update.bind(this);

    // This block is moved to a different location on mobile to match the designs
    // On mobile, this would be right before the first upsell h3.
    this.mobileAnchorEl = document.querySelector(
      ".MattressProductForm__OptionLabel.MattressProductForm__OptionLabel--upsell"
    );

    // On desktop it would be the Need Help? area.
    this.desktopAnchorEl = document.querySelector(".NeedHelpArea");

    this.items = this.querySelectorAll(".AlsoIncludesList__Item");
    this.redCircle = this.querySelector(".AlsoIncludesList__OptOutCross");
  }

  attachListeners() {
    this.mql.addListener(this.mqlListener);
    document.addEventListener("state:option-changed", this.updateOptOutAdded);
    document.addEventListener("state:addons-changed", this.updateOptOutAdded);

    // Add popup listeners to items within
    this.items.forEach((item) => {
      const openPopup = (e) => {
        const contentsEl = item.querySelector("[data-ail-popup-html]");
        GlobalNPopup.openWithContents("generic", contentsEl.innerHTML);
      };
      this.popupListeners.push({ item, handler: openPopup });
      item.addEventListener("click", openPopup);
    });
  }

  detachListeners() {
    this.mql.removeListener(this.mqlListener);
    document.removeEventListener(
      "state:option-changed",
      this.updateOptOutAdded
    );
    document.removeEventListener(
      "state:addons-changed",
      this.updateOptOutAdded
    );

    this.popupListeners.forEach((listener) => {
      listener.item.removeEventListener("click", listener.handler);
    });
    this.popupListeners = [];
  }

  /**
   * If an opt out product is marked to be added, reveal the red circle with cross
   */
  updateOptOutAdded() {
    if (window.NolahPDPState.isOptOutAdded) {
      this.redCircle.classList.remove("AlsoIncludesList__OptOutCross--hidden");
    } else {
      this.redCircle.classList.add("AlsoIncludesList__OptOutCross--hidden");
    }
  }

  showViewBasedOnWidth() {
    if (this.mql.matches) {
      this.showMobileView();
    } else {
      this.showDesktopView();
    }
  }

  update(e) {
    // If mobile
    if (e.matches) {
      this.showMobileView();

      // If desktop
    } else {
      this.showDesktopView();
    }
  }

  showMobileView() {
    if (this.mobileAnchorEl) {
      this.parentNode.insertBefore(this, this.mobileAnchorEl);
    }
  }

  showDesktopView() {
    if (this.desktopAnchorEl) {
      this.parentNode.insertBefore(this, this.desktopAnchorEl);
    }
  }
}
customElements.define("also-includes-list", AlsoIncludesList);

/**
 * Currently says 30% off and is used only on mattresses.
 */
class DrapedSavingsBadge extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.updateSalesText();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.updateSalesText = this.updateSalesText.bind(this);

    // This is a tspan element in an svg
    this.savingsEl = this.querySelector("[data-savings-number-el]");
  }

  attachListeners() {
    document.addEventListener("state:option-changed", this.updateSalesText);
    document.addEventListener("state:addons-changed", this.updateSalesText);
  }

  detachListeners() {
    document.removeEventListener("state:option-changed", this.updateSalesText);
    document.removeEventListener("state:addons-changed", this.updateSalesText);
  }

  updateSalesText() {
    const totalPrice = window.NolahPDPState.totalPrice;
    const totalComparePrice = window.NolahPDPState.totalComparePrice;

    const savingsAmt = totalComparePrice - totalPrice;
    if (savingsAmt > 0) {
      this.style.opacity = "1";
    } else {
      this.style.opacity = "0";
    }
  }
}
customElements.define("draped-savings-badge", DrapedSavingsBadge);

/**
 * This atc btn shows a different label when upsells are selected.
 */
class MattressATCBtn extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      this.updateBtnText();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;

      this.detachListeners();
    }
  }

  cacheElements() {
    this.update = this.update.bind(this);
    this.updateBtnText = this.updateBtnText.bind(this);
    this.atcClickHandler = this.atcClickHandler.bind(this);

    // The contents of the text change if on mobile
    this.mql = window.matchMedia("(max-width: 768px)");
    this.isShowingMobileView = this.mql.matches;

    this.button = this.querySelector(".MattressProductATCBtn__Btn");
  }

  attachListeners() {
    this.mql.addListener(this.update);
    this.button.addEventListener("click", this.atcClickHandler);
    document.addEventListener("state:option-changed", this.updateBtnText);
    document.addEventListener("state:addons-changed", this.updateBtnText);
    document.addEventListener("mulberry-warranty-changed", this.updateBtnText);
  }

  detachListeners() {
    this.mql.removeListener(this.update);
    this.button.removeEventListener("click", this.atcClickHandler);
    document.removeEventListener("state:option-changed", this.updateBtnText);
    document.removeEventListener("state:addons-changed", this.updateBtnText);
    document.removeEventListener(
      "mulberry-warranty-changed",
      this.updateBtnText
    );
  }

  update(e) {
    this.isShowingMobileView = e.matches;
  }

  atcClickHandler(e) {
    e.preventDefault();

    const items = this.getItemsToAdd();

    // Disable button
    this.button.setAttribute("disabled", "disabled");

    // Start loading bar
    document.dispatchEvent(new CustomEvent("theme:loading:start"));

    // Add to cart
    window.NolahCartManager.addItems(items)
      .catch((err) => {
        console.error("ATC Button", err);
      })
      .finally(() => {
        // Finish loading bar
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
        this.button.removeAttribute("disabled");
      });
  }

  /*
  updateBtnText() {
    if (this.isShowingMobileView) {
      this.button.innerHTML = "ADD TO CART";
    } else {
      // On desktop, the atc button is more specific
      const numItemsToBeAdded = window.NolahPDPState.numItemsToBeAdded;
      if (numItemsToBeAdded > 2) {
        this.button.innerHTML = `ADD ${numItemsToBeAdded - 1} ITEMS TO CART`;
      } else {
        this.button.innerHTML = "ADD MATTRESS TO CART";
      }
    }
  }
  */

  updateBtnText() {
    if (this.isShowingMobileView) {
      this.button.innerHTML = "ADD TO CART";
    } else {
      // On desktop, the atc button is more specific
      const numItemsToBeAdded = window.NolahPDPState.numItemsToBeAdded;
      const mattressProductCard = this.button.closest('.MattressProductCard');
      const mattressComesWithBlockLength = mattressProductCard.querySelectorAll('mattress-comes-with-block').length;
      const dynamicValue = numItemsToBeAdded - parseInt(mattressComesWithBlockLength, 10);
      
      if (numItemsToBeAdded > 2) {
        this.button.innerHTML = `ADD ${dynamicValue} ITEMS TO CART`;
      } else {
        this.button.innerHTML = "ADD TO CART";
      }
    }
  }

  /**
   * Gets the items array of mattresses, addons, gifts, and insurances to add.
   * Mattresses pages do not currently have a quantity selector, so addons
   * are counted as if only one mattress can be added at a time (or sometimes two if
   * the split king option is selected.)
   */
  getItemsToAdd() {
    const items = [];
    const currentVariant = window.NolahPDPState.currentVariant;
    const splitKingSelected = window.NolahPDPState.pseudoSplitKingSelected;

    // Get the mattresses to add (typically just the selected mattress, unless
    // split king is selected, in which there is additional logic to add Twin XLs).
    const mattressItems = this.getMattressItemsToAdd(currentVariant);
    mattressItems.forEach((i) => items.push(i));

    window.NolahPDPState.addonsSelected.forEach((addon) => {
      items.push({
        id: addon.id,
        quantity: 1,
      });
    });

    // Include the free gift product.
    if (window.freeGiftEnabled === true) {
   const excludedItems = [39436614697039, 39436614598735, 39436614631503, 39436614664271, 39436614729807, 39436614762575, 40733014556751];
  const freeGiftToAdd = this.getFreeGiftToAdd();
  if (freeGiftToAdd) {
    items.push(freeGiftToAdd);
  }
}

    
    // Include mulberry offer if selected (desktop only)
    if (
      window.innerWidth > 768 &&
      window.MulberryPDPState &&
      window.MulberryPDPState.selectedOffer &&
      window.MulberryMUtils
    ) {
      const warranty = window.MulberryPDPState.selectedOffer;

      // We add two warranty products if the pseudo split king size is selected.
      const getWarrantyItem = window.MulberryMUtils.getWarrantyItemObjToAdd;
      if (splitKingSelected) {
        const secondMattress =
          window.NolahPDPState.getSecondSplitMattressVariant();

        // If the firmnesses are the same, add two of the current variant.
        if (currentVariant.id === secondMattress.id) {
          items.push(getWarrantyItem(warranty, 2, currentVariant));

          // If they're not the same, then add each separately with different line item props.
        } else {
          items.push(getWarrantyItem(warranty, 1, currentVariant));
          items.push(getWarrantyItem(warranty, 1, secondMattress));
        }

        // Otherwise just add the warranty as normal
      } else {
        items.push(getWarrantyItem(warranty, 1, currentVariant));
      }
    }

    return items;
  }

  getFreeGiftToAdd() {
    if (!window.NolahPDPState.isOfferingFreeGift()) {
      return null;
    }

    if (window.location.href.includes("nolah-natural-11")) {
      return null;
    }

    const giftInfo = window.NolahPDPState.getMatchingGiftVariantInfo();
    if (giftInfo && giftInfo.variant) {
      if (giftInfo.variant.available) {
        // Indicate using properties what type of gift it is
        const propertiesToAdd = {};
        const isSplitKingGift = giftInfo.variant.options.some((o) =>
          o.includes("Split King")
        );
        if (isSplitKingGift) {
          // Split king sized gifts have a property _gosv1.
          // Currently the value is not used--only the key.
          propertiesToAdd._gosv1 = "2";
        } else {
          // All other gifts have a property _gov1. The value is not used.
          propertiesToAdd._gov1 = "1";
        }

        return {
          id: giftInfo.variant.id,
          quantity: giftInfo.quantity || 1,
          properties: propertiesToAdd,
        };
      } else {
        const currentSize = window.NolahPDPState.getOptionByName("Size");
        window.GlobalNPopup.openWithContents(
          "small-alert",
          `
          Sorry!<br>The free gift you have selected is sold out for the size ${currentSize}, and we were not able to add it to your cart. If you would like a free gift, please clear your cart and choose our pillows as a gift, which we have in stock.
        `
        );
        return null;
      }
    }
  }

  /**
   * Gets the mattress items, which is not straightforward if the pseudo split
   * king option is selected.
   */
  getMattressItemsToAdd(currentVariant) {
    const items = [];

    // Add two Twin XLs if the pseudo split king option is selected.
    if (window.NolahPDPState.pseudoSplitKingSelected) {
      const secondMattress =
        window.NolahPDPState.getSecondSplitMattressVariant();

      // If the sides are the same, add two of the current variant.
      if (currentVariant.id === secondMattress.id) {
        items.push({ id: currentVariant.id, quantity: 2 });

        // Otherwise, add each Twin XL separately.
      } else {
        items.push({ id: currentVariant.id, quantity: 1 });
        items.push({ id: secondMattress.id, quantity: 1 });
      }

      // If the pseudo split king isn't selected, just add the mattress like normal.
    } else {
      items.push({ id: currentVariant.id, quantity: 1 });
    }

    return items;
  }
}
customElements.define("mattress-atc-btn", MattressATCBtn);

class FinalCheckboxSummary extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      document.addEventListener("state:option-changed", this.updateCheckboxes);
      document.addEventListener("state:addons-changed", this.updateCheckboxes);
      document.addEventListener(
        "mulberry-warranty-changed",
        this.updateCheckboxes
      );
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;

      document.removeEventListener(
        "state:option-changed",
        this.updateCheckboxes
      );
      document.removeEventListener(
        "state:addons-changed",
        this.updateCheckboxes
      );
      document.removeEventListener(
        "mulberry-warranty-changed",
        this.updateCheckboxes
      );
    }
  }

  cacheElements() {
    // Though they are called checkboxes, they are just buttons
    this.checkboxes = Array.from(
      this.querySelectorAll(".CheckboxSummary__Checkbox")
    );

    this.updateCheckboxes = this.updateCheckboxes.bind(this);
  }

  updateCheckboxes() {
    this.uncheckAllExceptMattressAndGifts();

    const addons = window.NolahPDPState.addonsSelected;
    let hasPlatformBed = false;
    let hasAdjBase = false;
    addons.forEach((addon) => {
      // Note that there is a combined checkbox featured on pdps that offer both
      // the platform bed and the adj. base as upsells.
      // These type of pages will not have an individual checkbox representing
      // one adjustable base, or one platform bed.
      // Note we check for 'Platform B' since we sometimes the addon is called
      // Platform Bed and sometimes Platform Base.
      if (addon.name.includes("Platform B")) {
        hasPlatformBed = true;
        this.checkCheckboxWithName("platform-bed-adj-base");
        this.checkCheckboxWithName("platform-bed");
      } else if (addon.name.includes("Adjustable Base")) {
        hasAdjBase = true;
        this.checkCheckboxWithName("platform-bed-adj-base");
        this.checkCheckboxWithName("adjustable-base");
      } else if (addon.name.includes("Protector")) {
        this.checkCheckboxWithName("mattress-protector");
      } else if (addon.name.includes("Sheet Set")) {
        this.checkCheckboxWithName("bamboo-sheet-set");
      } else if (addon.name.includes("Removal and Setup")) {
        this.checkCheckboxWithName("mattress-removal-setup");
      } else if (addon.name.includes("Opt-Out")) {
        this.checkCheckboxWithName("remove-mattress-trial");
      }
    });

    // Update mulberry accident protection status
    if (window.MulberryPDPState && window.MulberryPDPState.selectedOffer) {
      this.checkCheckboxWithName("accident-protection");
    }

    // Adjust labels if needed for the aforementioned combined checkbox.
    if (hasPlatformBed && hasAdjBase) {
      this.relabelCheckbox("platform-bed-adj-base", "Platform Bed + Adj Base");
    } else if (hasPlatformBed) {
      this.relabelCheckbox("platform-bed-adj-base", "Platform Bed");
    } else if (hasAdjBase) {
      this.relabelCheckbox("platform-bed-adj-base", "Adjustable Base");
    } else {
      this.relabelCheckbox("platform-bed-adj-base", "Platform Bed / Adj Base");
    }

    // Update free gift checkbox to say "four" pillows if split king is selected
    if (window.NolahPDPState.pseudoSplitKingSelected) {
      this.relabelCheckbox(
        "two-free-squishy-pillows",
        "Four FREE Squishy Pillows"
      );
    } else {
      this.relabelCheckbox(
        "two-free-squishy-pillows",
        "Two FREE Squishy Pillows"
      );
    }

    // Update if hybrid foam is selected (og10 and sig12 only)
    if (window.HybridUpsellState) {
      if (window.HybridUpsellState.selectedValue === "hybrid") {
        this.relabelCheckbox("main-product", "All-Foam Hybrid Selected");
      } else {
        this.relabelCheckbox(
          "main-product",
          window.NolahProductJSON.product_alt_title
        );
      }
    }
  }

  uncheckAllExceptMattressAndGifts() {
    this.checkboxes.forEach((c) => {
      const itemName = c.dataset.itemName;
      const isMattress = c.dataset.isMattress === "true";
      if (!itemName.includes("free") && !isMattress) {
        this.setChecked(c, false);
      }
    });
  }

  /**
   * Safe to call on non-existing checkboxes (which is expected in some cases).
   */
  relabelCheckbox(name, newLabel) {
    const matchingCheckbox = this.checkboxes.find(
      (c) => c.dataset.itemName === name
    );
    if (matchingCheckbox) {
      const labelEl = matchingCheckbox.querySelector(
        ".CheckboxSummary__ItemName"
      );
      labelEl.innerHTML = newLabel;
    }
  }

  /**
   * Only works if a checkbox of that name is found (it's safe to call this
   * function on missing checkboxes, which would be the case if the combined
   * platform/adj.base checkbox is not used on a page.)
   */
  checkCheckboxWithName(name) {
    const matchingCheckbox = this.checkboxes.find(
      (c) => c.dataset.itemName === name
    );
    if (matchingCheckbox) {
      this.setChecked(matchingCheckbox, true);
    }
  }

  setChecked(checkbox, checked) {
    if (checked) {
      checkbox.classList.add("CheckboxSummary__Checkbox--added");
    } else {
      checkbox.classList.remove("CheckboxSummary__Checkbox--added");
    }
  }
}
customElements.define("final-checkbox-summary", FinalCheckboxSummary);

class OptionalsArea extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.updateSavingsInfo();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.updateCheckedAppearance = this.updateCheckedAppearance.bind(this);
    this.updateSavingsInfo = this.updateSavingsInfo.bind(this);
    this.removalPopupHandler = this.removalPopupHandler.bind(this);
    this.optOutPopupHandler = this.optOutPopupHandler.bind(this);

    this.removalPopupTrigger = this.querySelector(
      '.OptionalsArea__Line[data-name="removal-and-setup"]'
    );
    this.optOutPopupTrigger = this.querySelector(
      '.OptionalsArea__Line[data-name="opt-out-trial"]'
    );

    // The 'save additional $100' label on the right side
    this.optOutSavingsInfo = this.querySelector(
      '[data-name="opt-out-trial"] .OptionalsArea__SavingsInfo'
    );
  }

  attachListeners() {
    this.removalPopupTrigger.addEventListener(
      "click",
      this.removalPopupHandler
    );
    this.optOutPopupTrigger.addEventListener("click", this.optOutPopupHandler);
    document.addEventListener(
      "state:option-changed",
      this.updateCheckedAppearance
    );
    document.addEventListener(
      "state:addons-changed",
      this.updateCheckedAppearance
    );
    document.addEventListener("state:option-changed", this.updateSavingsInfo);
  }

  detachListeners() {
    this.removalPopupTrigger.removeEventListener(
      "click",
      this.removalPopupHandler
    );
    this.optOutPopupTrigger.removeEventListener(
      "click",
      this.optOutPopupHandler
    );
    document.removeEventListener(
      "state:option-changed",
      this.updateCheckedAppearance
    );
    document.removeEventListener(
      "state:addons-changed",
      this.updateCheckedAppearance
    );
    document.removeEventListener(
      "state:option-changed",
      this.updateSavingsInfo
    );
  }

  updateCheckedAppearance() {
    const addonsSelected = window.NolahPDPState.addonsSelected;
    let removalSetupAddonExists = false;
    let optOutAddonExists = false;
    addonsSelected.forEach((addon) => {
      if (addon.name.includes("Removal and Setup")) {
        this.removalPopupTrigger.classList.add("OptionalsArea__Line--added");
        removalSetupAddonExists = true;
      } else if (addon.name.includes("Opt-Out")) {
        this.optOutPopupTrigger.classList.add("OptionalsArea__Line--added");
        optOutAddonExists = true;
      }
    });

    // Remove checked styling if not checked
    if (!removalSetupAddonExists) {
      this.removalPopupTrigger.classList.remove("OptionalsArea__Line--added");
    }
    if (!optOutAddonExists) {
      this.optOutPopupTrigger.classList.remove("OptionalsArea__Line--added");
    }
  }

  updateSavingsInfo() {
    const amountSaved = window.NolahPDPState.optOutDiscountAmt;
    this.optOutSavingsInfo.innerHTML = `save additional ${NUtils.formatMoney(
      amountSaved
    )}`;
  }

  removalPopupHandler(e) {
    // If already selected, then remove the addon from being added
    if (
      this.removalPopupTrigger.classList.contains("OptionalsArea__Line--added")
    ) {
      window.NolahPDPState.removeAddonsWithNameContaining("Removal and Setup");

      // Early return
      return;
    }

    // Otherwise, open the popup
    const contentsEl = document.querySelector(
      "#product-mattress-removal-setup-popup"
    );
    GlobalNPopup.openWithContents("removal-and-setup", contentsEl.innerHTML);

    // Add click listeners on newly made add to order buttons
    const variantsJSON = JSON.parse(
      GlobalNPopup.popup.querySelector("[data-removal-and-setup-variants-json]")
        .innerHTML
    ).variants;
    const addToOrderBtns = GlobalNPopup.popup.querySelectorAll(
      ".MatRSPopup__ItemAddBtn"
    );
    addToOrderBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        // Disable the button immediately (no need to reenable later)
        btn.disabled = true;

        const variantID = btn.dataset.variantId;
        const matchingVariant = variantsJSON.find((v) => v.id == variantID);
        window.NolahPDPState.toggleAddon(matchingVariant);
        GlobalNPopup.close();
      });
    });
  }

  optOutPopupHandler(e) {
    // If already selected, then remove the addon from being added to cart
    if (
      this.optOutPopupTrigger.classList.contains("OptionalsArea__Line--added")
    ) {
      window.NolahPDPState.removeAddonsWithNameContaining("Opt-Out");

      // Early return
      return;
    }

    const contentsEl = document.querySelector(
      "#product-mattress-opt-out-popup"
    );
    GlobalNPopup.openWithContents("opt-out-trial", contentsEl.innerHTML);

    // Set up the add btn inside the newly populated popup
    const popupEl = GlobalNPopup.popup;
    const addBtn = popupEl.querySelector(".MatOptOutPopup__ItemAddBtn");
    addBtn.addEventListener("click", (e) => {
      // Disable the button immediately (no need to reenable later)
      addBtn.disabled = true;
      window.NolahPDPState.addOptOutTrial();
      GlobalNPopup.close();
    });

    // Init the amount saved label within the popup to accurate numbers
    const amountSavedLabel = popupEl.querySelector("[data-amount-saved-label]");
    const amountSaved = window.NolahPDPState.optOutDiscountAmt;
    amountSavedLabel.innerHTML = `${NUtils.formatMoney(amountSaved)}`;
  }
}
customElements.define("optionals-area", OptionalsArea);

class MPTotalSavings extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.syncToCurrentVariant();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.syncToCurrentVariant = this.syncToCurrentVariant.bind(this);
  }

  syncToCurrentVariant() {
    const totalPrice = window.NolahPDPState.totalPrice;
    const totalComparePrice = window.NolahPDPState.totalComparePrice;

    // Returns 0 if there's no free gift
    const freeGiftValue = window.NolahPDPState.getMatchingGiftValue();

    const savingsAmt = totalComparePrice - totalPrice + freeGiftValue;
    if (savingsAmt > 0) {
      this.innerHTML = `Total savings: ${NUtils.formatMoney(savingsAmt)}`;
    } else {
      this.innerHTML = "";
    }
  }

  attachListeners() {
    document.addEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
    document.addEventListener(
      "state:addons-changed",
      this.syncToCurrentVariant
    );
  }

  detachListeners() {
    document.removeEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
    document.removeEventListener(
      "state:addons-changed",
      this.syncToCurrentVariant
    );
  }
}
customElements.define("mp-total-savings", MPTotalSavings);

class MPIncludingLabel extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      // This label is not used if the mattress isn't including free gifts.
      if (!window.NolahPDPState.isOfferingFreeGift()) {
        this.remove();
        return;
      }

      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.syncToCurrentVariant();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.syncToCurrentVariant = this.syncToCurrentVariant.bind(this);
  }

  /**
   * Split king variants receive two sets of two pack pillows.
   */
  syncToCurrentVariant() {
    const freeGiftValue = window.NolahPDPState.getMatchingGiftValue();
    this.innerHTML = `Including your FREE gift (${NUtils.formatMoney(
      freeGiftValue
    )} value)`;
  }

  attachListeners() {
    document.addEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
    document.addEventListener(
      "state:addons-changed",
      this.syncToCurrentVariant
    );
  }

  detachListeners() {
    document.removeEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
    document.removeEventListener(
      "state:addons-changed",
      this.syncToCurrentVariant
    );
  }
}
customElements.define("mp-including-label", MPIncludingLabel);

/**
 * Miscellaneous mattress product page code that doesn't fit in a custom element.
 */
class MattressProductPage {
  constructor() {
    this.mql = window.matchMedia("(max-width: 768px)");
    this.mql.addListener(this.initOrDestroySwiper.bind(this));
    if (this.mql.matches) {
      this.initMainProductSwiper();
    }

    this.initMiniInfoBarPopups();
    this.initHelpMeDecideBtn();
  }

  initOrDestroySwiper(e) {
    // On mobile
    if (e.matches) {
      this.initMainProductSwiper();

      // On desktop
    } else {
      this.swiper.destroy();
    }
  }

  initMainProductSwiper() {
    this.swiper = new Swiper(".MPCMainSlider.swiper-container", {
      slidesPerView: 1,
      roundLengths: true,
      pagination: {
        el: ".MPCMainSlider__Pagination",
        type: "bullets",
        clickable: true,
      },
      breakpoints: {},
    });
  }

  initMiniInfoBarPopups() {
    const items = document.querySelectorAll(".MPMiniBar__Item");
    items.forEach((item) => {
      item.addEventListener("click", (e) => {
        const contentsEl = item.querySelector("[data-mibmp-popup-html]");
        if (contentsEl) {
          GlobalNPopup.openWithContents("generic", contentsEl.innerHTML);
        }
      });
    });
  }

  initHelpMeDecideBtn() {
    const btn = document.querySelector("[data-open-help-decide]");
    btn &&
      btn.addEventListener("click", (e) => {
        const contentsEl = document.querySelector(
          "#product-mattress-help-me-decide"
        );

        window.GlobalNPopup.loadWithContents(
          "need-help-deciding",
          contentsEl.innerHTML
        );

        // Replace any dynamic text in the description
        const description = window.GlobalNPopup.popup.querySelector(
          ".MatHMDPopup__Paragraph"
        );
        description.innerHTML = description.innerHTML.replace(
          "DYNAMIC:NOLAH_MATTRESS_NAME",
          window.NolahProductJSON.product.title
        );

        window.GlobalNPopup.open();
      });
  }
}

new MattressProductPage();
